import { formatCurrency } from 'renderer/shared/formatters/formatters';
import { isOrderScopeDiscount } from 'renderer/features/payments-page/cart/cartTotals';
import { getBasePrice } from 'renderer/features/payments-page/cart/cartPricing';

/** Bump when wording/math changes — stored with consent (CA ARL). */
export const AUTO_RENEWAL_DISCLOSURE_VERSION = '2026-07-23-each';

export const formatBillingInterval = (product: any): string => {
  const interval = product?.interval || 'year';
  const count = product?.intervalCount || 1;
  return count > 1 ? `${count} ${interval}s` : interval;
};

/**
 * What Stripe will charge on renewals for one subscription line:
 * - no discount / duration once → list × qty
 * - forever + item scope → (list − coupon) × qty (every copy keeps it)
 * - forever + order scope → list × qty − the line's share of the flat order
 *   amount (dollars) or list × qty × (1 − p%) (percent applies whole)
 * (Ignores temporary product sales — renewals follow list + coupon.)
 *
 * `orderShare` is this line's share of a flat order-scope dollar amount,
 * precomputed across the cart by buildAutoRenewalDisclosureLines.
 */
export function getSubscriptionRenewalPrice(item: any, orderShare = 0): number {
  const product = item?.productId;
  const qty = Number(item?.quantity) || 0;
  if (!product || qty <= 0) return 0;

  const listUnit = getBasePrice(product, item.hostApplication);
  const listTotal = listUnit * qty;
  const discount = item.discountId;

  if (!discount || discount.duration !== 'forever') {
    return listTotal;
  }

  if (isOrderScopeDiscount(discount)) {
    if (discount.amountUnits === 'percent') {
      return Math.max(0, listTotal * (1 - Number(discount.amount) / 100));
    }
    if (discount.amountUnits === 'dollars') {
      return Math.max(0, listTotal - orderShare);
    }
    return listTotal;
  }

  // Item scope: full coupon on every copy.
  let unitOff = 0;
  if (discount.amountUnits === 'percent') {
    unitOff = listUnit * (Number(discount.amount) / 100);
  } else if (discount.amountUnits === 'dollars') {
    unitOff = Number(discount.amount) || 0;
  } else {
    return listTotal;
  }

  return Math.max(0, listUnit - unitOff) * qty;
}

/**
 * Split flat order-scope forever dollar amounts across the subscription lines
 * that carry each code (even per copy, mirroring checkout apportionment).
 */
function computeOrderDollarShares(items: any[]): Map<any, number> {
  const shares = new Map<any, number>();
  const byCode = new Map<string, any[]>();

  (items || []).forEach((item: any) => {
    const d = item?.discountId;
    if (
      !d ||
      d.duration !== 'forever' ||
      !isOrderScopeDiscount(d) ||
      d.amountUnits !== 'dollars'
    ) {
      return;
    }
    if (item.productId?.category !== 'Subscription') return;
    const codeKey = d._id?.toString?.() || d.code || 'discount';
    byCode.set(codeKey, [...(byCode.get(codeKey) || []), item]);
  });

  byCode.forEach((lines) => {
    const amountCents = Math.round(
      (Number(lines[0].discountId.amount) || 0) * 100,
    );
    const totalCopies = lines.reduce(
      (sum, l) => sum + (Number(l.quantity) || 1),
      0,
    );
    if (totalCopies <= 0) return;
    const perCopyCents = Math.floor(amountCents / totalCopies);
    lines.forEach((l) => {
      shares.set(l, (perCopyCents * (Number(l.quantity) || 1)) / 100);
    });
  });

  return shares;
}

/**
 * One disclosure line per subscription cart line. Forever promos reduce the
 * disclosed renewal total; once-duration codes do not.
 *
 * Multi-copy lines quote the per-copy renewal ("$149.00 each per year");
 * different products / hosts / renewal prices stay on separate lines.
 */
export const buildAutoRenewalDisclosureLines = (items: any[]): string[] => {
  const orderShares = computeOrderDollarShares(items || []);

  const lines = (items || [])
    .filter((item: any) => item.productId?.category === 'Subscription')
    .map((item: any) => {
      const product = item.productId;
      const qty = item.quantity || 1;
      const renewalTotal = getSubscriptionRenewalPrice(
        item,
        orderShares.get(item) ?? 0,
      );
      const renewalEach =
        qty > 0 ? +(renewalTotal / qty).toFixed(2) : renewalTotal;
      const hostName = item.hostApplication?.displayName?.trim();
      const hostPart = hostName ? ` — ${hostName}` : '';
      const copies = qty > 1 ? ` (${qty} copies)` : '';
      const pricePart =
        qty > 1
          ? `${formatCurrency(renewalEach)} each`
          : formatCurrency(renewalEach);
      return `${
        product.name
      }${hostPart}${copies} automatically renews at ${pricePart} per ${formatBillingInterval(
        product,
      )} until you cancel.`;
    });

  lines.push(
    'You can cancel anytime from your Account Dashboard (Licenses & Subscriptions → Cancel) to stop future charges. We will email you a reminder before each renewal.',
  );
  return lines;
};
